import React, { FC, MouseEvent, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { OrderTag } from 'models/tags';
import AssignTagInOrderDialog from 'components/shared/assign-tag-in-order-dialog/assign-tag-in-order-dialog.component';
import { assignOrderTagTemplate } from 'apollo/templates/order-tags';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';

type OrderListTagComponentProps = {
    updateOrderTag: (tags: OrderTag[]) => void;
    orderId: string;
    orderTags: OrderTag[];
};

const OrderListTagComponent: FC<OrderListTagComponentProps> = ({
    updateOrderTag,
    orderId,
    orderTags,
}: OrderListTagComponentProps) => {
    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [rerender, setRerender] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [assignOrderTag] = useMutation(assignOrderTagTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const handleEditTagClick = () => {
        setIsAddEditOpen(true);
        setRerender(true);
    };

    const handleUpdateTag = async (tags: OrderTag[]) => {
        const ids = tags.map((tag) => tag._id);
        const { data } = await assignOrderTag({
            variables: {
                orderId: orderId,
                tagIds: ids,
            },
        });
        updateOrderTag(data?.assignOrderTag?.orderTags ?? tags);
        handleCloseAddEdit();
    };

    const handleCloseAddEdit = () => {
        setRerender(false);
        setIsAddEditOpen(false);
    };

    const handleEventBubbling = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const sortedTags = orderTags ?? [];
    const isSingleTag = sortedTags.length === 1;
    const isMultipleTags = sortedTags.length > 1;

    const tagListPopoverContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
            {sortedTags.map((tag) => (
                <Typography key={tag._id} variant="caption" sx={{ color: tag.color }}>
                    <strong>{tag.name}</strong>
                </Typography>
            ))}
        </Box>
    );

    const tooltipComponentsProps = {
        tooltip: {
            sx: {
                backgroundColor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                opacity: 1,
                px: 1.5,
                py: 0.75,
            },
        },
    };

    return (
        <div onClick={handleEventBubbling}>
            {isSingleTag && (
                <Tooltip
                    title={tagListPopoverContent}
                    placement="bottom-start"
                    componentsProps={tooltipComponentsProps}
                >
                    <Typography
                        onClick={handleEditTagClick}
                        variant="caption"
                        sx={{
                            cursor: 'pointer',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: sortedTags[0].color,
                        }}
                    >
                        <strong>{sortedTags[0].name}</strong>
                    </Typography>
                </Tooltip>
            )}
            {isMultipleTags && (
                <Tooltip
                    title={tagListPopoverContent}
                    placement="bottom-start"
                    componentsProps={tooltipComponentsProps}
                >
                    <Box
                        onClick={handleEditTagClick}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                        }}
                    >
                        {sortedTags.map((tag) => (
                            <CircleIcon key={tag._id} sx={{ fontSize: 12, color: tag.color, flexShrink: 0 }} />
                        ))}
                    </Box>
                </Tooltip>
            )}
            <AssignTagInOrderDialog
                open={isAddEditOpen}
                tags={orderTags}
                onAddUpdate={handleUpdateTag}
                onDialogClose={handleCloseAddEdit}
                rerender={rerender}
            />
        </div>
    );
};

export default OrderListTagComponent;
